import { useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { Divider } from '@mui/material';

import {
  Card,
  CardActions,
  CardHeader,
  Typography,
  Button,
  LinearProgress,
} from '@occc-app/design-system';
import { User_Role } from '@occc/graphql';

import { FeatureFlagRow } from './feature-flag-row.component';
import { useFeatureAccessForm, type FeatureAccessFormValues } from './use-feature-access-form.hook';

type FeatureFlagEditorProps = {
  userUuid: string;
  currentUserUuid: string;
  targetUserRole: User_Role;
  onCancel: () => void;
};

export function FeatureFlagEditor({
  userUuid,
  currentUserUuid,
  targetUserRole,
  onCancel,
}: FeatureFlagEditorProps) {
  const { flags, initialValues, onSubmit } = useFeatureAccessForm(userUuid);

  const renderForm = useCallback(
    ({ handleSubmit, submitting, pristine }: FormRenderProps<FeatureAccessFormValues>) => {
      const actions = [
        <Button key='cancel' variant='outlined' color='primary' onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key='save'
          type='submit'
          variant='contained'
          color='primary'
          disabled={pristine || submitting}
        >
          Save
        </Button>,
      ];

      return (
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader
              title={
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  Feature Access
                </Typography>
              }
              subheader={
                <Typography variant='body1' color='text.primary' sx={{ mt: 1 }}>
                  Override feature availability for this user. These settings grant additional
                  access beyond what the user's role and global configuration already provide.
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            {flags.map((flag, index) => (
              <div key={flag.key}>
                {index > 0 && <Divider />}
                <FeatureFlagRow
                  flag={flag}
                  userUuid={userUuid}
                  currentUserUuid={currentUserUuid}
                  targetUserRole={targetUserRole}
                />
              </div>
            ))}
            {submitting && <LinearProgress />}
            <Divider />
            <CardActions>{actions}</CardActions>
          </Card>
        </form>
      );
    },
    [flags, userUuid, currentUserUuid, targetUserRole, onCancel],
  );

  return (
    <Form<FeatureAccessFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={renderForm}
    />
  );
}
